// Given a section from `SECTIONS` and the current form data, return
// whether every required field is filled AND any section-specific
// business rules are satisfied (partner venue end-time, break-even,
// end date > start date, etc).
//
// Kept as a pure function so both the "Continue" button disabling and
// the pre-submit guard can reuse it without duplicating logic.

import { computeEventHours } from "./calculations";

export const requiredFieldsFilled = (section, formData) => {
  const baseValid = section.fields
    .filter((f) => f.required)
    .every((f) => {
      const val = formData[f.id];
      if (f.type === "image") return !!val;
      if (f.type === "interests") return Array.isArray(val) && val.length > 0;
      if (f.type === "address-search") {
        if (formData.partnerVenueId) return true;
        return !!formData.location && formData.location.trim().length > 0;
      }
      return String(val ?? "").trim().length > 0;
    });

  // Partner venues are billed hourly, so we need a known duration →
  // end time becomes required, and end must come after start.
  if (section.id === "schedule" && formData.partnerVenueId) {
    if (!String(formData.endTime ?? "").trim()) return false;
    if (formData.startDate && formData.endDate) {
      const startIso = `${formData.startDate}T${formData.startTime || "00:00"}`;
      const endIso = `${formData.endDate}T${formData.endTime || "00:00"}`;
      if (new Date(endIso) <= new Date(startIso)) return false;
    }
  }

  // Partner venue events must charge a positive ticket price AND the
  // tickets required to cover the venue minimum must fit inside
  // capacity, otherwise the event can't possibly confirm.
  if (section.id === "pricing" && formData.partnerVenueId) {
    const ticketPrice = Number(formData.cost) || 0;
    if (!(ticketPrice > 0)) return false;

    const pricePerHour = Number(formData._partnerMinPricePerHour) || 0;
    const hours = computeEventHours(formData);
    const minRevenue = hours * pricePerHour;
    const capacity = Number(formData.maxMembers) || 0;
    if (minRevenue > 0 && capacity > 0) {
      const ticketsNeeded = Math.ceil(minRevenue / ticketPrice);
      if (ticketsNeeded > capacity) return false;
    }
  }

  return baseValid;
};
